import { supabase } from './supabase';
import { deleteBucketFolder } from './storage';
import { logger } from '../utils/logger';

/**
 * Safely extracts the featured or first image URL from a system object
 * @param system - The system object containing photos
 * @returns The image URL or a fallback placeholder
 */
export function getSystemImageUrl(system: System): string {
  // Ensure photos is a valid array
  if (!system?.photos || !Array.isArray(system.photos) || system.photos.length === 0) {
    return '/theater.svg'; // Fallback to default image
  }

  // Try to find featured photo first
  const featuredPhoto = system.photos.find(photo => photo && typeof photo === 'object' && photo.featured && photo.url);
  if (featuredPhoto?.url) {
    return featuredPhoto.url;
  }

  // Fall back to first photo with valid URL
  const firstValidPhoto = system.photos.find(photo => photo && typeof photo === 'object' && photo.url);
  if (firstValidPhoto?.url) {
    return firstValidPhoto.url;
  }

  // Final fallback
  return '/theater.svg';
}

export interface System {
  id: number;
  title: string;
  description: string;
  category: string;
  location: string;
  specs: string[];
  photos: {
    url: string;
    featured?: boolean;
  }[];
  featured: boolean;
  featured_order: number | null;
  created_at: string;
}

export async function fetchFeaturedSystems(): Promise<System[]> {
  try {
    const { data, error } = await supabase
      .from('systems')
      .select('*')
      .eq('featured', true)
      .order('featured_order', { ascending: true });

    if (error) {
      logger.error('Error fetching featured systems:', error);
      throw new Error('Failed to fetch featured systems');
    }

    return data || [];
  } catch (error) {
    throw error;
  }
}

export async function deleteSystem(id: number): Promise<void> {
  try {
    // Get system data first to get the title for folder deletion
    const { data: system, error: fetchError } = await supabase
      .from('systems')
      .select('title')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    // Delete the system's folder in storage
    if (system) {
      await deleteBucketFolder('gallery', `${system.title}-${id}`);
    }

    // Delete the system record
    const { error: deleteError } = await supabase
      .from('systems')
      .delete()
      .eq('id', id);

    if (deleteError) throw deleteError;
  } catch (error) {
    throw error;
  }
}

export async function updateSystemFeatured(
  id: number,
  featured: boolean,
  featured_order: number | null
): Promise<void> {
  try {
    const { error } = await supabase
      .from('systems')
      .update({ featured, featured_order })
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw error;
  }
}